// @flow
/* eslint camelcase: 0 */
import type Moment from "moment"

import mmbTrack from "mmb/services/tracking/mmbTrack"
import {
  PaymentModule,
  RequestSubmitAction,
  IframeSubmitAction,
  TokenUseAction,
  CredentialSuccessAction,
  FinishSubmitAction,
} from "mmb/services/tracking/constants/trackingEvents"
import { iframeProcessPayment } from "mmb/externals"
import type { Category } from "mmb/consts/additionalServices"
import { API_ACTIONS, API_CATEGORIES } from "mmb/consts/booking"
import * as Zooz from "common/services/payments/Zooz"
import { transport } from "mmb/services/bookings/bookingsApi"
import { SERVICES } from "mmb/consts/additionalServices"
import type { BookingId, Currency, ExtrasId, PriceTotal } from "mmb/types/primitiveTypes"

type CreditCard = {
  email: string,
  number?: string,
  holderName?: string,
  expirationMonth?: string,
  expirationYear?: string,
  cvvNumber?: string,
}

type AdditionalService = {
  id: number,
  payment: {
    token: string,
    amount: PriceTotal,
    currency: Currency,
    created: Moment,
  },
  category: Category,
}

type AdditionalServicePayment = {
  additionalService: AdditionalService,
  card: CreditCard,
  bid: BookingId,
  isSandbox: boolean,
  booked_at: string,
  isPCIPayment: boolean,
}

type RequestAdditionalServicePayment = {
  bid: BookingId,
  abid: ExtrasId,
  booked_at: string,
  category: Category,
}

type Options = {
  bid: BookingId,
  abid: ExtrasId,
}

type PaymentOptions = {
  request: {
    url: string,
    method: "POST" | "GET" | "DELETE" | "PATCH",
  },
  payment: {
    url: string,
    method: "POST" | "GET" | "DELETE" | "PATCH",
  },
  isMambo: boolean,
}

const CATEGORY_TO_BE_SERVICE = {
  [SERVICES.PRICE_CHANGE]: API_CATEGORIES.PRICE_CHANGES,
  [SERVICES.MEALS]: API_CATEGORIES.MEALS,
  [SERVICES.SEATING]: API_CATEGORIES.SEAT_ORDERS,
  [SERVICES.INSURANCE]: API_CATEGORIES.INSURANCES,
  [SERVICES.BAGGAGE]: API_CATEGORIES.BAGGAGE,
}

const transformGW = gateName => {
  if (gateName === "payu") return "pos"
  if (gateName === "zooz") return "tactus"
  return gateName
}

const paymentOptions = (category: Category, options: Options): PaymentOptions => {
  switch (category) {
    case SERVICES.PRICE_CHANGE:
    case SERVICES.MEALS:
    case SERVICES.INSURANCE:
    case SERVICES.SEATING:
    case SERVICES.BAGGAGE:
      // eslint-disable-next-line no-case-declarations
      const backendCategory = CATEGORY_TO_BE_SERVICE[category]
      return {
        request: {
          url: `/mmb/v1/bookings/${options.bid}/${backendCategory}/${options.abid}/payments`,
          method: "POST",
        },
        payment: {
          url: `/mmb/v1/bookings/${options.bid}/${backendCategory}/${options.abid}/payments`,
          method: "PATCH",
        },
        isMambo: true,
      }
    case SERVICES.ASSISTED_REFUND:
      return {
        request: {
          url: `/mmb/v1/bookings/${options.bid}/assisted_refund`,
          method: "POST",
        },
        payment: {
          url: `/mmb/v1/bookings/${options.bid}/assisted_refund/${options.abid}/payments`,
          method: "PATCH",
        },
        isMambo: true,
      }

    default:
      return {
        request: {
          url: `/api/v0.1/users/self/bookings/${options.bid}`,
          method: "POST",
        },
        payment: {
          url: `/api/v0.1/users/self/bookings/${options.bid}`,
          method: "POST",
        },
        isMambo: false,
      }
  }
}

export const requestAdditionalServicePayment = ({
  bid,
  abid,
  booked_at,
  category,
}: RequestAdditionalServicePayment): Promise<any> => {
  const options = paymentOptions(category, { bid, abid })

  const payload = !options.isMambo
    ? {
        action: API_ACTIONS.REQUEST_PAYMENT,
        abid,
        booked_at,
      }
    : null

  mmbTrack({ module: PaymentModule, action: RequestSubmitAction }, { options, payload })
  return transport(options.request.method, bid, options.request.url, null, payload)
}

const requestPaymentCredentials = async ({
  isPCIPayment,
  isSandbox,
  paymentToken,
  card,
  additionalService,
  bid,
}: {
  isPCIPayment: boolean,
  isSandbox: boolean,
  paymentToken: string,
  card: CreditCard,
  additionalService: AdditionalService,
  bid: BookingId,
}) => {
  if (isPCIPayment) {
    const payload = {
      programId: "skypicker_new",
      sandbox: isSandbox,
      token: paymentToken,
      gate: transformGW("zooz"), // TODO update when others are available
      email: card.email,
      // phone: params.contact.phone,
      // state: POSState,
      // country: POSCountry,
    }

    mmbTrack({ module: PaymentModule, action: IframeSubmitAction }, { payload })

    const { token, encrypted_cvv } = await iframeProcessPayment(
      payload,
      // params.paymentSpecialInfo, // Not implemented in MMB
    )
    return {
      method_token: token,
      cvv: encrypted_cvv,
    }
  }
  const { paymentMethodToken } = await Zooz.sendPayment({
    paymentToken,
    email: card.email,
    number: card.number,
    name: card.holderName,
    expirationMonth: card.expirationMonth,
    expirationYear: card.expirationYear,
    cvv: card.cvvNumber,
    isSandbox,
    bid,
    abid: additionalService.id,
    finalCashInCurrency: additionalService.payment.amount,
    currency: additionalService.payment.currency,
    // Add authToken when implementing skymock
  })
  return {
    method_token: paymentMethodToken,
    cvv: card.cvvNumber,
  }
}

export function additionalServicePayment({
  additionalService,
  card,
  bid,
  booked_at,
  isSandbox,
  isPCIPayment,
}: AdditionalServicePayment): Promise<void> {
  // eslint-disable-next-line consistent-return
  return new Promise(async (resolve, reject) => {
    try {
      const abid = additionalService.id

      let paymentToken

      const isTokenValid =
        additionalService.payment.created &&
        additionalService.payment.created
          .clone()
          .add(15, "minutes")
          .isAfter()

      if (isTokenValid) {
        paymentToken = additionalService.payment.token
      } else {
        const response = await requestAdditionalServicePayment({
          bid,
          abid,
          booked_at,
          category: additionalService.category,
        })

        paymentToken = response.payment_token
      }

      mmbTrack({ module: PaymentModule, action: TokenUseAction }, { isTokenValid })

      const paymentCredentials = await requestPaymentCredentials({
        isPCIPayment,
        isSandbox,
        card,
        additionalService,
        bid,
        paymentToken,
      })

      mmbTrack({ module: PaymentModule, action: CredentialSuccessAction })

      const options = paymentOptions(additionalService.category, { bid, abid })

      const payload = options.isMambo
        ? {
            finish: paymentCredentials,
          }
        : {
            action: API_ACTIONS.FINISH_PAYMENT,
            abid,
            payment: paymentCredentials,
          }

      mmbTrack({ module: PaymentModule, action: FinishSubmitAction }, { options })

      return transport(options.payment.method, bid, options.payment.url, null, payload)
        .then(resolve)
        .catch(reject)
    } catch (err) {
      reject(err)
    }
  })
}
